import * as React from "react";
import { useQuery } from "react-query";
import logoSrc from "../assets/images/logo.png";
import { getIcons, getQuestions } from "../utils/question";
import styled from "styled-components/macro";
import { termEnquiry } from "../utils/enquiry";
import { getAgeFromDate } from "../utils/helpers";
import { useUrlQuery } from "../utils";

const FullScreenCenter = styled.div`
  min-height: 100vh;
  min-width: 100vw;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export function FullPageLoader() {
  return (
    <FullScreenCenter>
      <img
        src={logoSrc}
        alt="fyntune"
        css={`
          width: 12em;
        `}
      />
    </FullScreenCenter>
  );
}

const RecommendationContext = React.createContext();
RecommendationContext.displayName = "RecommendationContext";

function useRecommendation() {
  const recommendationContext = React.useContext(RecommendationContext);
  if (!recommendationContext)
    throw new Error(
      `useRecommendation must be in scope with <RecommendationProvider>`,
    );

  return recommendationContext;
}

const localStorageAnswers = localStorage.getItem("answers");

const subDomainBrokers = {
  "elephant-reco": "alliance",
  "paybima-reco": "paybima",
  "term-reco": "fyntune",
};

const DEV_BROKER = "fyntune";

function RecommendationProvider({ children }) {
  const urlQuery = useUrlQuery();
  const enquiryId = urlQuery.get("enquiryId");
  const source = urlQuery.get("source");
  const subDomain = window.location.hostname.split(".")[0];
  const broker =
    process.env.NODE_ENV === "development"
      ? DEV_BROKER
      : subDomainBrokers[subDomain];

  let { status, data: questions } = useQuery(
    "questions",
    () => getQuestions(broker),
    {
      retry: false,
    },
  );

  let { status: iconsStatus, data: icons } = useQuery("icons", getIcons);

  const { status: termEnquiryStatus, refetch } = useQuery(
    "term-enquiry",
    () =>
      termEnquiry({
        enquiryId,
      }),
    { enabled: false },
  );

  const [answers, setAnswers] = React.useState(
    localStorageAnswers ? JSON.parse(localStorageAnswers) : {},
  );

  React.useEffect(() => {
    localStorage.setItem("answers", JSON.stringify(answers));
  }, [answers]);

  React.useEffect(() => {
    if (enquiryId && source === "fyntune")
      refetch().then(res => {
        if (res.data?.data) {
          const { input, name, email } = res.data.data;
          if (input) {
            setAnswers({
              name,
              mail: email,
              age: getAgeFromDate(input.dob),
              identify: input.gender === "M" ? "male" : "female",
              work: input.occupation,
              income: input.term_annual_income,
            });
          }
        }
      });
  }, [enquiryId, refetch, source]);

  if (status === "error")
    return <FullScreenCenter>Error Fething Questions</FullScreenCenter>;

  if (
    ["loading", "idle"].includes(status) ||
    ["loading", "idle"].includes(iconsStatus) ||
    ["loading"].includes(termEnquiryStatus)
  )
    return <FullPageLoader />;

  const value = {
    questions,
    answers,
    setAnswers,
    icons,
    broker,
  };

  return (
    <RecommendationContext.Provider value={value}>
      {children}
    </RecommendationContext.Provider>
  );
}

export { RecommendationProvider, useRecommendation };
